import { useEffect, useSyncExternalStore } from 'react';
import { pushState, type PushState } from './push';

/**
 * Gemeinsamer Push-Zustand dieses Geraets.
 *
 * Einladung (PushPrompt) und Karte (PushCard) stehen auf derselben Seite und
 * muessen dasselbe wissen: Wer oben einschaltet, sieht unten sofort die Karte;
 * wer in der Karte ausschaltet, sieht nicht beides verschwinden.
 */
export interface PushStatus {
  state: PushState | null;
  /** Die Erlaubnis wurde in dieser Sitzung gerade verweigert - dann steht die Anleitung bereit. */
  deniedAttempt: boolean;
  /** Zaehlt jede Aenderung - auch solche, die nur die Einladung betreffen. */
  tick: number;
}

let snapshot: PushStatus = { state: null, deniedAttempt: false, tick: 0 };
const listeners = new Set<() => void>();

function update(patch: Partial<Omit<PushStatus, 'tick'>>): void {
  snapshot = { ...snapshot, ...patch, tick: snapshot.tick + 1 };
  listeners.forEach((listener) => listener());
}

export function publishPushState(state: PushState): void {
  if (snapshot.state !== state) update({ state });
}

export function setPushDeniedAttempt(value: boolean): void {
  if (snapshot.deniedAttempt !== value) update({ deniedAttempt: value });
}

/** Etwas ausserhalb des Zustands hat sich geaendert (Einladung weggeklickt). */
export function notifyPushChange(): void {
  update({});
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => { listeners.delete(listener); };
}

export function usePushStatus(): PushStatus {
  const status = useSyncExternalStore(subscribe, () => snapshot);
  useEffect(() => {
    let active = true;
    void pushState().then((current) => { if (active) publishPushState(current); }).catch(() => undefined);
    return () => { active = false; };
  }, []);
  return status;
}

/** Nur fuer Tests. */
export function resetPushStatus(): void {
  snapshot = { state: null, deniedAttempt: false, tick: 0 };
  listeners.forEach((listener) => listener());
}
